import SidePanelHeader from "../components/SidePanelHeader"
import { useNavigate } from "react-router"
import * as Navigation from "../../../../../modules/Navigation";
import SidePanelInfo from "../components/SidePanelInfo"
import SidePanelAuditTrail from "../components/SidePanelAuditTrail";
import { EntityNames } from "../../../../../enums/EntityNames";
import { formatAddressString } from "../../../../../helpers/StringHelper";
import { Button } from "react-aria-components";
import { useState } from "react";
import undoPick from "../../../../../modules/apis/PickingApi";
import { movingSidePanelSignal } from "../../Layout";
import { modalSignal } from "../../../Modal/ModalContainer";
import ContinueModal from "../../../Modal/ConfirmModal";
import { RecallStatusSmartEnum } from "../../../../../enums/Domain/RecallStatusSmartEnum";
import { PermissionContextType, usePermissions } from "../../../../../modules/auth/Permissions/PermissionContext";
import { hasPermission } from "../../../../../modules/auth/Permissions/PermissionModule";
import { PermissionConcerns, PermissionTypes } from "../../../../../modules/auth/Permissions/PermissionStrings";
import ToolTipInfo from "../components/ToolTipInfo";
import { useMainConfig } from "../../../../../modules/config/MainConfigContext";
import { RecallsSchema } from "../../../entityComponents/Schemas/RecallsSchema";
import { hasVisibleChildren } from "../../../entityComponents/Schema";


function formatItemCount(value: number | null | undefined): string | number {
    if (value == null || value <= 0) {
        return 'TBA';
    }
    return value;
}

export default function SidePanelRecallsView({ data, onViewPage }: any) {
    const navigate = useNavigate();
    const permissionsContext = usePermissions();
    const { config } = useMainConfig();

    function viewEntity() {
        if (hasVisibleChildren(EntityNames.recalls, permissionsContext)) {
            Navigation.view.navigate(navigate, EntityNames.recalls, data.id, data)
        }
    }

    function viewEntityAndTab(tabName: string) {
        if (hasVisibleChildren(EntityNames.recalls, permissionsContext)) {
            const indexOfTab = RecallsSchema.children?.findIndex(x => x.entityName === tabName) ?? 0;
            Navigation.view.navigate(navigate, EntityNames.recalls, data.id, data, indexOfTab);
        }
    }

    const itemCountLabel = (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            Item Count
            {config?.itemCountExplanation && (
                <ToolTipInfo text={config.itemCountExplanation} tooltipId="item-count-tooltip" />
            )}
        </span>
    );

    return (<>
        {data &&
            <>
                {<SidePanelHeader descriptor="Recall" value={`${data.reference}`} statuses={[data.recallStatus]} onClick={!onViewPage ? () => viewEntity() : undefined} />}
                <Actions data={data} permissionsContext={permissionsContext} onViewPage={onViewPage} />
                <SidePanelInfo
                    values={[
                        { label: "Requested by", value: data.createdBy },
                        ...(data.onBehalfOf && data.onBehalfOf !== data.createdBy ? [{ label: "On behalf of", value: data.onBehalfOf }] : []),
                        { label: "Requested At", value: data.dateRequested ?? undefined },
                        { label: "Reference", value: data.reference },
                        { label: "Account", value: data.accountName },
                        { label: itemCountLabel, value: formatItemCount(data.itemCount) },
                    ]}
                />
                <SidePanelInfo
                    values={[
                        { label: "Cancellation Notes", value: data.cancellationNotes },
                    ]}
                    />
                <SidePanelInfo
                    values={[
                        { label: "Courier", value: data.courier },
                        { label: "Packaging", value: data.packaging },
                        { label: "Tracking code", value: data.trackingCode },
                        { label: "Tracking url", value: data.trackingUrl ? <a className="textWrap" target="_blank" href={data.trackingUrl}>{data.trackingUrl}</a> : undefined },
                    ]}
                    />
                <SidePanelInfo
                    values={[
                        { label: "Client Ref", value: data.orderNumber },
                        { label: "Invoice Address", value: formatAddressString(data.invoiceAddress), classname: "whitespace--pre-line" }
                    ]}
                    />
                <SidePanelInfo
                    values={[
                        { label: "Request Type", value: data.requestType },
                        { label: "Delivery Method", value: data.deliveryMethod },
                        { label: "Delivery Price", value: data.deliveryPrice },
                        { label: "Delivery Address", value: formatAddressString(data.deliveryAddress), classname: "whitespace--pre-line" },
                        { label: "Send To", value: data.sendToEmail },
                    ]}
                />
                {!onViewPage &&
                    hasPermission(permissionsContext.permissions!, PermissionTypes.read, PermissionConcerns.Inventory_Audit_Trails) &&
                    (<SidePanelAuditTrail entityName={EntityNames.recalls} id={data.id} viewFunction={() => viewEntityAndTab(EntityNames.audits)} />)}
            </>
        }
    </>)
}

function Actions({ data, permissionsContext, onViewPage }: { data: any, permissionsContext: PermissionContextType, onViewPage: boolean }) {

    return (
        <div className="display--flex gap--s flex__wrap--wrap margin__top--s">
            {data.recallStatus.value === RecallStatusSmartEnum.Draft.value && (hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Edit_Recall) || hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Make_Recall_Request)) && data.canEdit && <EditButton id={data.id} />}
            {hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Recalls) && data.recallStatus.value === RecallStatusSmartEnum.ToPack.value && onViewPage && <UndoButton id={data.id} onViewPage={onViewPage} />}
            {hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Recalls) && data.recallStatus.value === RecallStatusSmartEnum.ToPick.value && <PickButton id={data.id} />}
            {hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Recalls) && data.recallStatus.value === RecallStatusSmartEnum.ToPack.value && <PackButton id={data.id} />}
        </div>
    )
}

function UndoButton({ id, onViewPage }: { id: string, onViewPage: boolean }) {
    const [disabled, setDisabled] = useState<boolean>(false)

    async function UndoPick() {
        const handle = async () => {
            setDisabled(true)
            const result = await undoPick(id);
            if (result) {
                setDisabled(false)
                if (onViewPage) {
                    window.location.reload();
                } else {
                    movingSidePanelSignal.value = movingSidePanelSignal.value = { ...movingSidePanelSignal.value, show: false };
                }
            }
        }
        modalSignal.value = <ContinueModal heading={`Undo pick?`} information="This will allow you to re-submit the pick list" ContinueFunc={async () => { await handle() }} />
    }

    return (
        <Button className={"button button--small button--primary button--primary--destructive button--with-icon"} isDisabled={disabled} onPress={UndoPick}>
            <p className="button-text">Undo Pick</p>
        </Button>
    )
}

function EditButton({ id }: { id: string }) {
    const navigate = useNavigate()

    return (
        <Button className={"button button--small button--primary button--with-icon"} onPress={() => { Navigation.CreateRecalls.navigate(navigate, id) }}>
            <p className="button-text">Edit Recall</p>
        </Button>
    )
}

function PickButton({ id }: { id: string }) {
    const navigate = useNavigate()

    return (
        <Button className={"button button--small button--primary button--with-icon"} onPress={() => { Navigation.RecallsPicking.navigate(navigate, id) }}>
            <p className="button-text">Start Picking</p>
        </Button>
    )
}

function PackButton({ id }: { id: string }) {
    const navigate = useNavigate()

    return (
        <Button className={"button button--small button--primary button--with-icon"} onPress={() => { Navigation.RecallsPacking.navigate(navigate, id) }}>
            <p className="button-text">Start Packing</p>
        </Button>
    )
}